import 'css/src/data/transcript/nodes/DragHandleElementNode.module.scss';

import type { LexicalNode, NodeKey, SerializedElementNode, Spread } from 'lexical';
import { ElementNode } from 'lexical';

export enum DragHandleType {
    Right = 'RIGHT',
    Left = 'LEFT',
}

export type SerializedDragHandleElementNode = Spread<
    {
        handleType: DragHandleType;
        type: 'drag-handle-node';
        version: 1;
    },
    SerializedElementNode
>;

export const DragHandleElementNodeTextContent = '\u00A0'; // non-breaking space to give some width
/**
 * @see OverlayElementNode for tree structure in lexical
 */
export class DragHandleElementNode extends ElementNode {
    __handleType: DragHandleType;

    constructor(handleType: DragHandleType, key?: NodeKey) {
        super(key);
        this.__handleType = handleType;
    }

    static override getType(): string {
        return 'drag-handle-node';
    }

    static override clone(node: DragHandleElementNode): DragHandleElementNode {
        return new DragHandleElementNode(node.__handleType, node.__key);
    }

    override createDOM(): HTMLElement {
        const handle = document.createElement('button');
        handle.className = 'drag-handle m-l5-0 p-l5-0';
        handle.contentEditable = 'false'; // prevent the user from adding text inside of the drag handle
        if (this.__handleType === DragHandleType.Left) {
            handle.className = handle.className.concat(' left');
        } else if (this.__handleType === DragHandleType.Right) {
            handle.className = handle.className.concat(' right');
        }
        handle.setAttribute('draggable', 'true');
        const barNode = document.createElement('span');
        barNode.className = 'visual-bar';
        barNode.textContent = DragHandleElementNodeTextContent;
        handle.appendChild(barNode);
        return handle;
    }

    override updateDOM(): boolean {
        return false;
    }

    static override importJSON(
        serializedNode: SerializedDragHandleElementNode,
    ): DragHandleElementNode {
        const node = $createDragHandleElementNode(serializedNode.handleType);
        return node;
    }

    override exportJSON(): SerializedDragHandleElementNode {
        return {
            ...super.exportJSON(),
            handleType: this.handleType,
            type: 'drag-handle-node',
            version: 1,
        };
    }

    get handleType(): DragHandleType {
        return this.__handleType;
    }

    override canInsertTextBefore(): boolean {
        // don't allow lexical to create a new span as a direct sibling of the drag handle
        // if the user places the cursor right before the drag handle and starts typing
        return false;
    }

    override canInsertTextAfter(): boolean {
        // don't allow lexical to create a new span as a direct sibling of the drag handle
        // if the user places the cursor right after the drag handle and starts typing
        return false;
    }

    override remove(): void {
        // don't allow the user to remove the drag handle using the backspace/delete keys
        return undefined;
    }
}

export function $isDragHandleElementNode(node: LexicalNode): node is DragHandleElementNode {
    return node instanceof DragHandleElementNode;
}

export function $createDragHandleElementNode(handleType: DragHandleType): DragHandleElementNode {
    return new DragHandleElementNode(handleType);
}
